import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import ptBR from '@/locales/pt-BR.json';
import en from '@/locales/en.json';

export type Locale = 'pt-BR' | 'en';

const translations = {
  'pt-BR': ptBR,
  'en': en,
};

export function detectBrowserLanguage(): Locale {
  const stored = localStorage.getItem('locale');
  if (stored && (stored === 'pt-BR' || stored === 'en')) {
    return stored as Locale;
  }

  const browserLang = navigator.language;
  if (browserLang.startsWith('pt')) {
    return 'pt-BR';
  }
  return 'en';
}

interface TranslationContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export const TranslationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => detectBrowserLanguage());

  useEffect(() => {
    // Ensure the locale is set correctly on mount
    const detected = detectBrowserLanguage();
    if (detected !== locale) {
      setLocaleState(detected);
    }
  }, []);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('locale', newLocale);
  };

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: Record<string, unknown> | string = translations[locale];
    
    for (const k of keys) {
      if (typeof value === 'object' && value !== null) {
        value = value[k] as Record<string, unknown> | string;
      }
    }
    
    return (typeof value === 'string' ? value : key);
  };

  return (
    <TranslationContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </TranslationContext.Provider>
  );
};

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
}
